import { useCallback } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { FindReplacePlugin } from '@atlaskit/editor-plugins/find-replace';
import type { UndoRedoPlugin } from '@atlaskit/editor-plugins/undo-redo';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';

export const useEditorActions = (
	editorFullPagePresetAPI: PublicPluginAPI<[UndoRedoPlugin, FindReplacePlugin]> | undefined,
): {
	canUndo: boolean;
	canRedo: boolean;
	undo: () => boolean;
	redo: () => boolean;
	activateFindReplace: () => boolean;
} => {
	const historyCanUndo = useSharedPluginStateSelector(editorFullPagePresetAPI, 'history.canUndo');
	const historyCanRedo = useSharedPluginStateSelector(editorFullPagePresetAPI, 'history.canRedo');

	const canUndo =
		(editorFullPagePresetAPI?.undoRedoPlugin?.actions.undo && historyCanUndo) || false;
	const canRedo =
		(editorFullPagePresetAPI?.undoRedoPlugin?.actions.redo && historyCanRedo) || false;

	const undo = useCallback(() => {
		if (!editorFullPagePresetAPI?.undoRedoPlugin?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.undoRedoPlugin.actions.undo();
	}, [editorFullPagePresetAPI]);

	const redo = useCallback(() => {
		if (!editorFullPagePresetAPI?.undoRedoPlugin?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.undoRedoPlugin.actions.redo();
	}, [editorFullPagePresetAPI]);

	const activateFindReplace = useCallback(() => {
		if (!editorFullPagePresetAPI?.findReplace?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.findReplace.actions.activateFindReplace();
	}, [editorFullPagePresetAPI]);

	return {
		canUndo,
		canRedo,
		undo,
		redo,
		activateFindReplace,
	};
};
